import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { da } from "date-fns/locale";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useStripeConnect } from "@/hooks/useStripeConnect";
import { useTermsAcceptance } from "@/hooks/useTermsAcceptance";
import TermsAcceptanceDialog from "@/components/TermsAcceptanceDialog";
import { maskAddressForMap } from "@/lib/addressMasking";
import { AlertCircle, CalendarDays, CheckCircle2, CreditCard, ShieldCheck } from "lucide-react";
import listingPlaceholder from "@/assets/listing-placeholder.svg";

type NavState = {
  listing?: any;              // fra ParkingSpotDetail handleBooking
  selectedDates?: Date[];
  totalPrice?: number;        // visning på detail
  bookingDays?: number;
};

function sortDates(dates: Date[]) {
  return [...dates].sort((a, b) => a.getTime() - b.getTime());
}

function areConsecutiveDays(sorted: Date[]) {
  if (sorted.length <= 1) return true;
  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(sorted[i - 1]); prev.setHours(0,0,0,0);
    const cur  = new Date(sorted[i]);     cur.setHours(0,0,0,0);
    const diff = (cur.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24);
    if (diff !== 1) return false;
  }
  return true;
}

function startEndFromDates(dates: Date[]) {
  const s = new Date(dates[0]);
  s.setHours(0, 0, 0, 0);
  const e = new Date(dates[dates.length - 1]);
  e.setHours(23, 59, 59, 999);
  return { start: s, end: e };
}

export default function Book() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as NavState;

  // Hvis man går direkte til /book/:id uden state -> vi henter listing igen
  const { data: listingFromDb } = useQuery({
    queryKey: ["listing", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select("id,title,address,base_price_day,photos,facilities,size,owner_id")
        .eq("id", id)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const listing = state.listing || listingFromDb;
  const selectedDates = (state.selectedDates || []) as Date[];

  const ownerId = listing?.owner_id as string | undefined;
  const { data: hostConnect, isLoading: hostConnectLoading } = useStripeConnect(ownerId);
  const hostPayoutsReady = hostConnect?.hasStripeAccount === true;

  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  
  const { hasAcceptedRenter, isLoading: termsLoading } = useTermsAcceptance();

  // Hent nuværende bruger ID
  useEffect(() => {
    const fetchUser = async () => {
      const { data: auth } = await supabase.auth.getUser();
      setCurrentUserId(auth?.user?.id || null);
    };
    fetchUser();
  }, []);

  const sorted = useMemo(() => sortDates(selectedDates), [selectedDates]);
  const consecutive = useMemo(() => areConsecutiveDays(sorted), [sorted]);
  const { start, end } = useMemo(() => {
    if (!sorted.length) return { start: null as any, end: null as any };
    return startEndFromDates(sorted);
  }, [sorted]);

  // Hent pris (i øre) fra DB, så total_amount passer til schema
  const pricePerDayOre =
    (listing?.base_price_day as number | undefined) ?? Math.round((state.listing?.price ?? 0) * 100);

  const days = sorted.length || 1;
  const totalAmount = pricePerDayOre * days;
  const platformFeePct = 15;
  // Platform fee is deducted from host's earnings, not added to renter's price
  const hostEarningsOre = Math.round(totalAmount * (100 - platformFeePct) / 100);
  const displayAddress = listing?.address ? maskAddressForMap(listing.address) : "Adresse vises efter bekræftet booking";

  useEffect(() => {
    if (!listing) return;
    if (currentUserId && ownerId && currentUserId === ownerId) {
      setErrorMsg("Du kan ikke booke din egen parkeringsplads.");
    } else if (!selectedDates?.length) {
      setErrorMsg("Vælg datoer på detaljesiden først.");
    } else if (!consecutive) {
      setErrorMsg("Datoerne skal ligge i forlængelse af hinanden uden huller.");
    } else {
      setErrorMsg(null);
    }
  }, [listing, selectedDates, consecutive, currentUserId, ownerId]);

  async function handlePay() {
    try {
      setBusy(true);
      setErrorMsg(null);

      // Kræv login
      const { data: auth } = await supabase.auth.getUser();
      if (!auth?.user) {
        navigate(`/auth?mode=signin&redirect=/book/${id}`);
        return;
      }

      // Tjek om lejer har accepteret vilkår
      if (!hasAcceptedRenter) {
        setShowTermsDialog(true);
        setBusy(false);
        return;
      }

      // Tjek om brugeren forsøger at booke sin egen plads
      if (currentUserId && ownerId && currentUserId === ownerId) {
        setErrorMsg("Du kan ikke booke din egen parkeringsplads.");
        return;
      }

      if (!id || !listing || !sorted.length || !consecutive) {
        setErrorMsg("Ugyldigt bookinguplæg. Prøv igen fra detaljesiden.");
        return;
      }

      // Stop tidligt hvis værten ikke har fuldført Stripe udbetalinger
      if (ownerId && hostConnectLoading === false && !hostPayoutsReady) {
        setErrorMsg("Udlejeren har ikke fuldført betalingsopsætningen endnu. Kontakt udlejeren eller prøv en anden plads.");
        return;
      }

      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          listing_id: id,
          start_date: start!.toISOString(),
          end_date: end!.toISOString(),
        }
      });
      
      if (error) throw error;
      
      if (data?.url) {
        const inIframe = window.self !== window.top;
        if (inIframe) {
          // Iframes (preview) block the Stripe redirect – open in a new tab instead
          const win = window.open(data.url, "_blank", "noopener,noreferrer");
          if (!win) {
            setCheckoutUrl(data.url);
            setErrorMsg("Din browser blokerede vinduet. Klik på linket nedenfor for at betale.");
          }
        } else {
          window.location.href = data.url;
        }
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (e: any) {
      setErrorMsg(e.message || "Kunne ikke starte betalingen. Prøv igen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-4 py-6 sm:py-10 max-w-5xl">
        <div className="mb-6 space-y-2">
          <Button variant="ghost" className="-ml-3" onClick={() => navigate(-1)}>
            Tilbage
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Bekræft din booking</h1>
          <p className="text-muted-foreground">Tjek datoer og pris, før du går videre til sikker betaling.</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
          <Card>
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl">Din parkeringsplads</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5 sm:space-y-6">
            {!listing ? (
              <div>Henter…</div>
            ) : (
              <>
                <div className="flex gap-3 sm:gap-4">
                  <div className="w-28 h-24 sm:w-40 sm:h-28 rounded-md overflow-hidden bg-muted flex-shrink-0">
                    <img
                      src={(listing.photos && listing.photos[0]) || listingPlaceholder}
                      alt={listing.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm sm:text-base truncate">{listing.title}</div>
                    <div className="text-xs sm:text-sm text-muted-foreground line-clamp-2">{displayAddress}</div>
                    <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                      <ShieldCheck className="h-3.5 w-3.5" /> Fuld adresse deles efter bekræftelse
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="rounded-md border bg-muted/30 p-4">
                  <div className="mb-2 flex items-center gap-2 text-sm font-medium"><CalendarDays className="h-4 w-4" /> Datoer</div>
                  {sorted.length ? (
                    <div className="text-foreground">
                      {format(sorted[0], "d. MMM yyyy", { locale: da })}
                      {sorted.length > 1 && (
                        <> – {format(sorted[sorted.length - 1], "d. MMM yyyy", { locale: da })}</>
                      )}
                      <span className="ml-2 text-muted-foreground">({days} dag{days > 1 ? "e" : ""})</span>
                    </div>
                  ) : (
                    <div className="text-foreground">Ingen datoer valgt</div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="text-muted-foreground">
                      {Math.round(pricePerDayOre / 100)} kr × {days} dag{days > 1 ? "e" : ""}
                    </span>
                    <span>{Math.round(totalAmount / 100)} kr</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between font-semibold text-base sm:text-lg">
                    <span>Total</span>
                    <span>{Math.round(totalAmount / 100)} kr</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Værten modtager ca. {Math.round(hostEarningsOre / 100)} kr efter {platformFeePct}% platform fee.
                  </div>
                </div>

                {errorMsg && (
                  <div className="flex gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>{errorMsg}</span>
                  </div>
                )}

                {checkoutUrl && (
                  <a
                    href={checkoutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-sm font-medium text-primary underline"
                  >
                    Åbn sikker betaling i nyt vindue
                  </a>
                )}

                <div className="flex flex-col gap-3">
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={handlePay}
                    disabled={!sorted.length || !consecutive || busy || termsLoading || (!!ownerId && !hostConnectLoading && !hostPayoutsReady)}
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    {busy ? "Starter betaling…" : "Gå til sikker betaling"}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

          <aside className="space-y-3 rounded-lg border bg-card p-5 text-sm h-fit lg:sticky lg:top-24">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-success" />
              <div><div className="font-medium">Sikker betaling</div><p className="text-muted-foreground">Betaling håndteres af Stripe.</p></div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-success" />
              <div><div className="font-medium">Ingen skjulte gebyrer</div><p className="text-muted-foreground">Platform fee trækkes fra værtens udbetaling.</p></div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-success" />
              <div><div className="font-medium">Privat adressebeskyttelse</div><p className="text-muted-foreground">Præcis adresse vises først efter bekræftet booking.</p></div>
            </div>
          </aside>
        </div>
      </main>

      <TermsAcceptanceDialog
        open={showTermsDialog}
        onOpenChange={setShowTermsDialog}
        requiredType="renter"
        onAccepted={handlePay}
      />
    </div>
  );
}
